/**
 * Workflows Page
 * Browse and manage AI-powered workflows
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AppLayout } from '../../components/layout';
import { workflowApi, WorkflowTemplate, Workflow as ApiWorkflow } from '../../services/api';
import { WorkflowEditor } from '../../components/workflow';
import { BackButton, Spinner, Input, Button } from '../../components/ui';
import { convertNodesToWorkflow, validateWorkflowData } from '../../components/workflow/WorkflowEditor/utils/workflowConverter';
import styles from './WorkflowsPage.module.css';
import { sanitizeBrandText } from '../../utils/helpers';
import { 
  Plus,
  Grid3x3,
  List,
  Users,
  Play,
  Settings,
  MoreHorizontal,
  Copy,
  Download,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  History,
  RefreshCw,
  Filter,
  Calendar,
  Eye,
  Zap,
  GitFork,
  LayoutGrid,
  User,
  ChevronDown,
} from 'lucide-react';

// Category icon mapping
const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'my-workflows': <User size={18} />,
  'all-workflows': <GitFork size={18} />,
  'templates': <LayoutGrid size={18} />,
};

// Categories for workflows
const CATEGORIES = [
  { id: 'my-workflows', label: 'My Workflows', count: 0 },
  { id: 'all-workflows', label: 'All Workflows', count: 0 },
  { id: 'templates', label: 'Templates', count: 0 },
];

// View modes
type ViewMode = 'list' | 'grid';

// Page modes
type PageMode = 'list' | 'template-details' | 'editor' | 'run-history';

// Run status types
type RunStatus = 'running' | 'completed' | 'failed' | 'cancelled' | 'pending' | 'initializing';

// Run stage status
type StageStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped';

// Workflow run interface
interface WorkflowRun {
  id: string;
  workflowId: string;
  workflowName: string;
  status: RunStatus;
  startedAt: string;
  completedAt?: string;
  duration?: number;
  triggeredBy: string;
  stages: WorkflowStage[];
  variables?: Record<string, any>;
  error?: string;
  currentStateId?: string;
}

// Workflow stage interface
interface WorkflowStage {
  id: string;
  name: string;
  status: StageStatus;
  startedAt?: string;
  completedAt?: string;
  duration?: number;
  output?: string;
  error?: string;
  assistantId?: string;
  iteration?: number;
  totalIterations?: number;
}

interface WorkflowsPageProps {}

export const WorkflowsPage: React.FC<WorkflowsPageProps> = () => {
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [workflows, setWorkflows] = useState<ApiWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('my-workflows');
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [pageMode, setPageMode] = useState<PageMode>('list');
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [templateCount, setTemplateCount] = useState(0);
  const [currentWorkflow, setCurrentWorkflow] = useState<ApiWorkflow | null>(null);
  const [selectedWorkflowForRuns, setSelectedWorkflowForRuns] = useState<ApiWorkflow | null>(null);
  const [workflowRuns, setWorkflowRuns] = useState<WorkflowRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<WorkflowRun | null>(null);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runningWorkflowId, setRunningWorkflowId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  const [selectedStageOutput, setSelectedStageOutput] = useState<WorkflowStage | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  
  // Ref for polling interval
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Mock workflow runs data (replace with actual API call)
  const mockWorkflowRuns: WorkflowRun[] = useMemo(() => [
    {
      id: 'run-001',
      workflowId: 'wf-001',
      workflowName: 'Java Migration',
      status: 'completed',
      startedAt: '2026-01-29T10:30:00Z',
      completedAt: '2026-01-29T10:45:00Z',
      duration: 900000,
      triggeredBy: 'user@example.com',
      stages: [
        { id: 'stage-1', name: 'Initializer', status: 'completed', startedAt: '2026-01-29T10:30:00Z', completedAt: '2026-01-29T10:32:00Z', duration: 120000, assistantId: 'mason' },
        { id: 'stage-2', name: 'Developer', status: 'completed', startedAt: '2026-01-29T10:32:00Z', completedAt: '2026-01-29T10:38:00Z', duration: 360000, assistantId: 'dev', iteration: 1, totalIterations: 5 },
        { id: 'stage-3', name: 'Checker', status: 'completed', startedAt: '2026-01-29T10:38:00Z', completedAt: '2026-01-29T10:42:00Z', duration: 240000, assistantId: 'dev' },
        { id: 'stage-4', name: 'Finalizer', status: 'completed', startedAt: '2026-01-29T10:42:00Z', completedAt: '2026-01-29T10:45:00Z', duration: 180000, assistantId: 'dev' },
      ],
    },
    {
      id: 'run-002',
      workflowId: 'wf-001',
      workflowName: 'Java Migration',
      status: 'running',
      startedAt: '2026-01-29T11:00:00Z',
      triggeredBy: 'user@example.com',
      stages: [
        { id: 'stage-1', name: 'Initializer', status: 'completed', startedAt: '2026-01-29T11:00:00Z', completedAt: '2026-01-29T11:02:00Z', duration: 120000, assistantId: 'mason' },
        { id: 'stage-2', name: 'Developer', status: 'running', startedAt: '2026-01-29T11:02:00Z', assistantId: 'dev', iteration: 3, totalIterations: 8 },
        { id: 'stage-3', name: 'Checker', status: 'pending', assistantId: 'dev' },
        { id: 'stage-4', name: 'Finalizer', status: 'pending', assistantId: 'dev' },
      ],
    },
    {
      id: 'run-003',
      workflowId: 'wf-001',
      workflowName: 'Java Migration',
      status: 'failed',
      startedAt: '2026-01-28T14:00:00Z',
      completedAt: '2026-01-28T14:15:00Z',
      duration: 900000,
      triggeredBy: 'ci-system',
      error: 'Build failed: Missing dependency com.example:library:1.0.0',
      stages: [
        { id: 'stage-1', name: 'Initializer', status: 'completed', startedAt: '2026-01-28T14:00:00Z', completedAt: '2026-01-28T14:02:00Z', duration: 120000, assistantId: 'mason' },
        { id: 'stage-2', name: 'Developer', status: 'failed', startedAt: '2026-01-28T14:02:00Z', completedAt: '2026-01-28T14:15:00Z', error: 'Build failed', assistantId: 'dev', iteration: 2, totalIterations: 5 },
        { id: 'stage-3', name: 'Checker', status: 'skipped', assistantId: 'dev' },
        { id: 'stage-4', name: 'Finalizer', status: 'skipped', assistantId: 'dev' },
      ],
    },
  ], []);

  // Fetch data based on selected category with timeout
  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    
    // Add timeout to prevent endless loading
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      if (selectedCategory === 'templates') {
        const response = await workflowApi.getTemplates({ search: searchQuery || undefined });
        if (response.success && response.data) {
          setTemplates(response.data.templates);
          setTemplateCount(response.data.total);
        }
      } else if (selectedCategory === 'my-workflows') {
        const response = await workflowApi.getMyWorkflows();
        if (response.success && response.data) {
          setWorkflows(response.data.items || []);
        }
      } else {
        const response = await workflowApi.getWorkflows({ search: searchQuery || undefined });
        if (response.success && response.data) {
          setWorkflows(response.data.items || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      setFetchError(error instanceof Error ? error.message : 'Failed to load workflows');
      // Clear stale data on error
      if (selectedCategory === 'templates') {
        setTemplates([]);
      } else {
        setWorkflows([]);
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setInitialLoad(false);
    }
  }, [selectedCategory, searchQuery]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load workflow runs (with optional silent refresh for polling)
  const loadWorkflowRuns = useCallback(async (workflow: ApiWorkflow, silent: boolean = false) => {
    if (!silent) {
      setRunsLoading(true);
    }
    try {
      const response = await workflowApi.listRuns(workflow.id);
      if (response.success && response.data) {
        // Map API response to WorkflowRun interface
        const apiRuns = response.data.items || response.data || [];
        
        // Transform API stateExecutions to UI stages format
        const mappedRuns: WorkflowRun[] = apiRuns.map((run: any) => {
          // Map completed state executions
          const completedStages = (run.stateExecutions || []).map((exec: any) => ({
            id: exec.stateId,
            name: exec.stateId, // State name
            status: exec.status as StageStatus,
            startedAt: exec.startedAt,
            completedAt: exec.completedAt,
            duration: exec.duration,
            output: typeof exec.output === 'string' ? exec.output : 
                   exec.assistantResponse || 
                   (exec.output ? JSON.stringify(exec.output, null, 2) : undefined),
            error: exec.error?.message || exec.error,
            assistantId: exec.assistantId,
            iteration: exec.iteration,
            totalIterations: exec.totalIterations,
          }));
          
          // If there's a current state that's running and not in stateExecutions, add it
          const currentStateId = run.currentStateId;
          const hasCurrentStateInExecutions = completedStages.some(
            (s: any) => s.id === currentStateId
          );
          
          let stages = completedStages;
          
          // Add currently running state if it's not already in the list
          if (currentStateId && !hasCurrentStateInExecutions && 
              (run.status === 'running' || run.status === 'initializing')) {
            stages = [
              ...completedStages,
              {
                id: currentStateId,
                name: currentStateId,
                status: 'running' as StageStatus,
                startedAt: run.startedAt, // Approximate
              }
            ];
          }
          
          // Derive actual status from stages if run shows as initializing but has execution data
          let derivedStatus = run.status as RunStatus;
          if ((run.status === 'initializing' || run.status === 'running') && stages.length > 0) {
            const hasFailedStage = stages.some((s: any) => s.status === 'failed');
            const allStagesCompleted = stages.every((s: any) => s.status === 'completed' || s.status === 'skipped');
            
            if (hasFailedStage) {
              derivedStatus = 'failed';
            } else if (allStagesCompleted && stages.length > 0) {
              derivedStatus = 'completed';
            }
          }
          
          return {
            id: run.id,
            workflowId: run.workflowId,
            workflowName: run.workflowName,
            status: derivedStatus,
            startedAt: run.startedAt,
            completedAt: run.completedAt,
            duration: run.duration,
            triggeredBy: run.userId || run.triggeredBy || 'system',
            error: run.error?.message || run.error,
            variables: run.variables,
            currentStateId: currentStateId,
            stages,
          };
        });
        
        setWorkflowRuns(mappedRuns.length > 0 ? mappedRuns : mockWorkflowRuns);
        
        // Update selectedRun if it exists in the new data
        if (selectedRun) {
          const updatedRun = mappedRuns.find(r => r.id === selectedRun.id);
          if (updatedRun) {
            setSelectedRun(updatedRun);
          }
        }
        
        return mappedRuns;
      } else {
        // Fallback to mock data if API returns empty
        setWorkflowRuns(mockWorkflowRuns);
        return mockWorkflowRuns;
      }
    } catch (error) {
      console.error('Failed to load workflow runs:', error);
      // Fallback to mock data on error for demo purposes
      if (!silent) {
        setWorkflowRuns(mockWorkflowRuns);
      }
      return [];
    } finally {
      if (!silent) {
        setRunsLoading(false);
      }
    }
  }, [mockWorkflowRuns, selectedRun]);

  // Check if any runs are in progress (running, pending, or initializing)
  const hasActiveRuns = useCallback((runs: WorkflowRun[]) => {
    return runs.some(run => 
      run.status === 'running' || 
      run.status === 'pending' || 
      run.status === 'initializing'
    );
  }, []);

  // Start polling for run updates
  const startPolling = useCallback((workflow: ApiWorkflow) => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setIsPolling(true);
    
    // Poll every 3 seconds for updates
    pollingIntervalRef.current = setInterval(async () => {
      const runs = await loadWorkflowRuns(workflow, true);
      
      // Stop polling if no active runs
      if (!hasActiveRuns(runs)) {
        stopPolling();
      }
    }, 3000);
  }, [loadWorkflowRuns, hasActiveRuns]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Auto-start polling when viewing run history with active runs
  useEffect(() => {
    if (pageMode === 'run-history' && selectedWorkflowForRuns && hasActiveRuns(workflowRuns)) {
      startPolling(selectedWorkflowForRuns);
    } else {
      stopPolling();
    }
    
    // Cleanup on unmount or when leaving run history
    return () => {
      stopPolling();
    };
  }, [pageMode, selectedWorkflowForRuns, workflowRuns, hasActiveRuns, startPolling, stopPolling]);

  // Run a workflow (using full execution engine)
  const handleRunWorkflow = useCallback(async (workflow: ApiWorkflow) => {
    setRunningWorkflowId(workflow.id);
    setNotification({ type: 'info', message: `Starting workflow "${workflow.name}"...` });
    
    try {
      // Use executeWorkflow for stage-by-stage execution
      const response = await workflowApi.executeWorkflow(workflow.id);
      
      if (response.success) {
        setNotification({ type: 'success', message: `Workflow "${workflow.name}" started successfully! Executing stages...` });
        
        // Refresh workflows list to update run count
        fetchData();
        
        // If we're viewing run history for this workflow, refresh runs
        if (selectedWorkflowForRuns?.id === workflow.id) {
          loadWorkflowRuns(workflow);
          // Start polling for updates
          startPolling(workflow);
        }
        
        // Show run history for the workflow and start polling
        setTimeout(() => {
          setSelectedWorkflowForRuns(workflow);
          loadWorkflowRuns(workflow);
          setPageMode('run-history');
          // Start polling for updates
          startPolling(workflow);
        }, 1000);
      } else {
        setNotification({ type: 'error', message: `Failed to run workflow: ${response.error || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('Failed to run workflow:', error);
      setNotification({ type: 'error', message: `Failed to run workflow: ${error instanceof Error ? error.message : 'Unknown error'}` });
    } finally {
      setRunningWorkflowId(null);
      // Auto-hide notification after 5 seconds
      setTimeout(() => setNotification(null), 5000);
    }
  }, [fetchData, selectedWorkflowForRuns, loadWorkflowRuns, startPolling]);

  // Handle view run history
  const handleViewRunHistory = (workflow: ApiWorkflow) => {
    setSelectedWorkflowForRuns(workflow);
    loadWorkflowRuns(workflow);
    setPageMode('run-history');
  };

  // Format duration
  const formatDuration = (ms?: number): string => {
    if (!ms) return '--';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Get status icon
  const getStatusIcon = (status: RunStatus | StageStatus) => {
    switch (status) {
      case 'completed': return <CheckCircle2 size={16} className={styles.statusIconCompleted} />;
      case 'running': return <span className={styles.statusIconRunning}><Spinner size="sm" /></span>;
      case 'initializing': return <span className={styles.statusIconRunning}><Spinner size="sm" /></span>;
      case 'failed': return <XCircle size={16} className={styles.statusIconFailed} />;
      case 'cancelled': return <XCircle size={16} className={styles.statusIconCancelled} />;
      case 'pending': return <Clock size={16} className={styles.statusIconPending} />;
      case 'skipped': return <AlertCircle size={16} className={styles.statusIconSkipped} />;
      default: return <Clock size={16} />;
    }
  };

  // Handle template click
  const handleTemplateClick = async (template: WorkflowTemplate) => {
    setSelectedTemplate(template);
    setPageMode('template-details');
  };

  // Handle create from template
  const handleCreateFromTemplate = async (template: WorkflowTemplate) => {
    try {
      const response = await workflowApi.createWorkflow({
        name: template.name,
        description: template.description,
        templateId: template.id,
        isSharedWithProject: true,
      });
      if (response.success && response.data) {
        setCurrentWorkflow(response.data);
        setPageMode('editor');
      }
    } catch (error) {
      console.error('Failed to create workflow:', error);
    }
  };

  // Handle save workflow from editor
  const handleSaveWorkflow = async (nodes: any[], edges: any[]) => {
    if (!currentWorkflow) return;
    
    try {
      // Convert React Flow nodes/edges to proper backend format
      // This generates: assistants[], states[], metaStates[], edges[]
      const workflowData = convertNodesToWorkflow(nodes, edges);
      
      // Validate before saving
      const validationErrors = validateWorkflowData(workflowData);
      if (validationErrors.length > 0) {
        console.warn('Workflow validation warnings:', validationErrors);
        // Don't block save, just warn - user might be in progress
      }
      
      // Send properly formatted data to backend
      await workflowApi.updateWorkflow(currentWorkflow.id, {
        assistants: workflowData.assistants,
        states: workflowData.states,
        metaStates: workflowData.metaStates,
        edges: workflowData.edges,
      } as Partial<ApiWorkflow>);
      
      setNotification({ type: 'success', message: 'Workflow saved successfully!' });
      setTimeout(() => setNotification(null), 3000);
      
      console.log('Workflow saved successfully', {
        assistants: workflowData.assistants.length,
        states: workflowData.states.length,
        metaStates: workflowData.metaStates.length,
        edges: workflowData.edges.length,
      });
    } catch (error) {
      console.error('Failed to save workflow:', error);
      setNotification({ type: 'error', message: 'Failed to save workflow' });
      setTimeout(() => setNotification(null), 5000);
      throw error; // Re-throw to let caller know save failed
    }
  };

  // Handle save and run workflow from editor
  const handleSaveAndRunWorkflow = async (nodes: any[], edges: any[]) => {
    if (!currentWorkflow) return;
    
    try {
      // First save the workflow
      await handleSaveWorkflow(nodes, edges);
      
      // Then execute it using the full execution engine
      setNotification({ type: 'info', message: `Starting workflow "${currentWorkflow.name}"...` });
      
      const runResponse = await workflowApi.executeWorkflow(currentWorkflow.id);
      
      if (runResponse.success) {
        setNotification({ type: 'success', message: `Workflow "${currentWorkflow.name}" started! Executing stages...` });
        
        // Navigate to run history after a brief delay
        setTimeout(() => {
          setSelectedWorkflowForRuns(currentWorkflow);
          loadWorkflowRuns(currentWorkflow);
          setPageMode('run-history');
        }, 1500);
      } else {
        setNotification({ type: 'error', message: `Failed to run workflow: ${runResponse.error || 'Unknown error'}` });
      }
    } catch (error) {
      console.error('Failed to save and run workflow:', error);
      setNotification({ type: 'error', message: `Failed to save and run: ${error instanceof Error ? error.message : 'Unknown error'}` });
      throw error;
    } finally {
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Handle back navigation
  const handleBack = () => {
    if (pageMode === 'template-details') {
      setSelectedTemplate(null);
      setPageMode('list');
    } else if (pageMode === 'editor') {
      setCurrentWorkflow(null);
      setPageMode('list');
    } else if (pageMode === 'run-history') {
      setSelectedWorkflowForRuns(null);
      setSelectedRun(null);
      setWorkflowRuns([]);
      setPageMode('list');
    }
  };

  // Render template card
  const renderTemplateCard = (template: WorkflowTemplate) => (
    <div 
      key={template.id} 
      className={styles.templateCard}
      onClick={() => handleTemplateClick(template)}
    >
      <div className={styles.templateIcon}>
        {template.iconUrl ? (
          <img src={template.iconUrl} alt={template.name} />
        ) : (
          <div className={styles.defaultIcon}><Zap size={28} /></div>
        )}
      </div>
      <div className={styles.templateContent}>
        <h3 className={styles.templateName}>{template.name}</h3>
        <p className={styles.templateAuthor}>by {sanitizeBrandText(template.author)}</p>
        <p className={styles.templateDescription}>{template.description}</p>
        <div className={styles.templateFooter}>
          <button 
            className={styles.addButton}
            onClick={(e) => {
              e.stopPropagation();
              handleCreateFromTemplate(template);
            }}
          >
            <Plus size={16} />
          </button>
          <div className={styles.sharedBadge}>
            <Users size={12} />
            <span>Shared with Project</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Render workflow card
  const renderWorkflowCard = (workflow: ApiWorkflow) => {
    const status = workflow.status || 'draft';
    const statusClassName = styles[status] || '';
    
    const handleCardClick = () => {
      setCurrentWorkflow(workflow);
      setPageMode('editor');
    };
    
    return (
      <div 
        key={workflow.id} 
        className={styles.workflowCard}
        onClick={handleCardClick}
      >
        <div className={styles.workflowIcon}>
          {workflow.iconUrl ? (
            <img src={workflow.iconUrl} alt={workflow.name} />
          ) : (
            <div className={styles.defaultIcon}><Zap size={28} /></div>
          )}
        </div>
        <div className={styles.workflowContent}>
          <h3 className={styles.workflowName}>{workflow.name || 'Untitled Workflow'}</h3>
          {workflow.description && (
            <p className={styles.workflowDescription}>{workflow.description}</p>
          )}
          <div className={styles.workflowMeta}>
            <span className={`${styles.statusBadge} ${statusClassName}`}>
              {status}
            </span>
            <span className={styles.runCount}>{workflow.runCount ?? 0} runs</span>
          </div>
          <div className={styles.workflowActions}>
            <button 
              className={`${styles.actionButton} ${runningWorkflowId === workflow.id ? styles.running : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleRunWorkflow(workflow);
              }}
              disabled={runningWorkflowId === workflow.id}
            >
              {runningWorkflowId === workflow.id ? (
                <>
                  <Spinner size="sm" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Run
                </>
              )}
            </button>
            <button 
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleViewRunHistory(workflow);
              }}
            >
              <History size={16} />
              History
            </button>
            <button 
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentWorkflow(workflow);
                setPageMode('editor');
              }}
            >
              <Settings size={16} />
              Edit
            </button>
            <button 
              className={styles.moreButton}
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render run history page
  const renderRunHistory = () => {
    if (!selectedWorkflowForRuns) return null;

    return (
      <div className={styles.runHistoryPage}>
        {/* Run History Header */}
        <div className={styles.runHistoryHeader}>
          <div className={styles.runHistoryHeaderLeft}>
            <BackButton onClick={handleBack} />
            <div className={styles.runHistoryTitle}>
              <h1>{selectedWorkflowForRuns.name}</h1>
              <span className={styles.runHistorySubtitle}>Run History</span>
            </div>
          </div>
          <div className={styles.runHistoryHeaderRight}>
            <button 
              className={styles.refreshButton} 
              onClick={() => loadWorkflowRuns(selectedWorkflowForRuns)}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
            <button 
              className={`${styles.runWorkflowButton} ${runningWorkflowId === selectedWorkflowForRuns.id ? styles.running : ''}`}
              onClick={() => handleRunWorkflow(selectedWorkflowForRuns)}
              disabled={runningWorkflowId === selectedWorkflowForRuns.id}
            >
              {runningWorkflowId === selectedWorkflowForRuns.id ? (
                <>
                  <Spinner size="sm" />
                  Running...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Run Workflow
                </>
              )}
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.runHistoryContent}>
          {/* Runs List Panel */}
          <div className={styles.runsListPanel}>
            <div className={styles.runsListHeader}>
              <h3>All Runs</h3>
              <div className={styles.runsFilter}>
                <Filter size={14} />
                <span>Filter</span>
              </div>
            </div>

            {runsLoading ? (
              <div className={styles.runsLoading}>
                <Spinner size="md" label="Loading runs..." />
              </div>
            ) : (
              <div className={styles.runsList}>
                {workflowRuns.map((run) => (
                  <div
                    key={run.id}
                    className={`${styles.runItem} ${selectedRun?.id === run.id ? styles.selected : ''}`}
                    onClick={() => setSelectedRun(run)}
                  >
                    <div className={styles.runItemHeader}>
                      <div className={styles.runItemStatus}>
                        {getStatusIcon(run.status)}
                        <span className={`${styles.runStatusText} ${styles[run.status]}`}>
                          {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                        </span>
                      </div>
                      <span className={styles.runItemId}>#{run.id.slice(-4)}</span>
                    </div>
                    <div className={styles.runItemMeta}>
                      <span className={styles.runItemDate}>
                        <Calendar size={12} />
                        {formatDate(run.startedAt)}
                      </span>
                      <span className={styles.runItemDuration}>
                        <Clock size={12} />
                        {formatDuration(run.duration)}
                      </span>
                    </div>
                    <div className={styles.runItemTriggeredBy}>
                      Triggered by {run.triggeredBy}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Run Details Panel */}
          <div className={styles.runDetailsPanel}>
            {selectedRun ? (
              <>
                <div className={styles.runDetailsPanelHeader}>
                  <h3>Run Details</h3>
                  <span className={styles.runId}>Run #{selectedRun.id.slice(-4)}</span>
                </div>

                {/* Run Summary */}
                <div className={styles.runSummary}>
                  <div className={styles.runSummaryItem}>
                    <span className={styles.runSummaryLabel}>Status</span>
                    <div className={`${styles.runSummaryStatus} ${styles[selectedRun.status]}`}>
                      {getStatusIcon(selectedRun.status)}
                      <span>{selectedRun.status.charAt(0).toUpperCase() + selectedRun.status.slice(1)}</span>
                    </div>
                  </div>
                  <div className={styles.runSummaryItem}>
                    <span className={styles.runSummaryLabel}>Started</span>
                    <span className={styles.runSummaryValue}>{formatDate(selectedRun.startedAt)}</span>
                  </div>
                  <div className={styles.runSummaryItem}>
                    <span className={styles.runSummaryLabel}>Duration</span>
                    <span className={styles.runSummaryValue}>{formatDuration(selectedRun.duration)}</span>
                  </div>
                  <div className={styles.runSummaryItem}>
                    <span className={styles.runSummaryLabel}>Triggered By</span>
                    <span className={styles.runSummaryValue}>{selectedRun.triggeredBy}</span>
                  </div>
                </div>

                {/* Error Message */}
                {selectedRun.error && (
                  <div className={styles.runError}>
                    <AlertCircle size={16} />
                    <span>{selectedRun.error}</span>
                  </div>
                )}

                {/* Stages Timeline */}
                <div className={styles.stagesSection}>
                  <h4>Execution Stages</h4>
                  <div className={styles.stagesTimeline}>
                    {selectedRun.stages.length === 0 ? (
                      <div className={styles.stagesEmpty}>
                        {(selectedRun.status === 'initializing' || selectedRun.status === 'running') ? (
                          <>
                            <Spinner size="sm" />
                            <span>Initializing workflow execution...</span>
                          </>
                        ) : (
                          <span>No execution stages recorded</span>
                        )}
                      </div>
                    ) : (
                      selectedRun.stages.map((stage, index) => (
                        <div key={stage.id} className={`${styles.stageItem} ${styles[stage.status]}`}>
                          <div className={styles.stageConnector}>
                            <div className={styles.stageDot}>
                              {getStatusIcon(stage.status)}
                            </div>
                            {index < selectedRun.stages.length - 1 && (
                              <div className={`${styles.stageLine} ${styles[stage.status]}`} />
                            )}
                          </div>
                          <div className={styles.stageContent}>
                            <div className={styles.stageHeader}>
                              <span className={styles.stageName}>{stage.name}</span>
                              {stage.iteration && stage.totalIterations && (
                              <span className={styles.stageIteration}>
                                Iteration {stage.iteration}/{stage.totalIterations}
                              </span>
                            )}
                          </div>
                          <div className={styles.stageMeta}>
                            {stage.assistantId && (
                              <span className={styles.stageAssistant}>
                                Assistant: {stage.assistantId}
                              </span>
                            )}
                            {stage.duration && (
                              <span className={styles.stageDuration}>
                                {formatDuration(stage.duration)}
                              </span>
                            )}
                          </div>
                          {stage.error && (
                            <div className={styles.stageError}>
                              {stage.error}
                            </div>
                          )}
                          {stage.status === 'running' && (
                            <div className={styles.stageProgress}>
                              <div className={styles.progressBar}>
                                <div className={styles.progressFill} />
                              </div>
                              <span>In progress...</span>
                            </div>
                          )}
                          {/* Show output preview for completed stages */}
                          {stage.output && stage.status === 'completed' && (
                            <div className={styles.stageOutputPreview}>
                              <pre>{stage.output.substring(0, 200)}{stage.output.length > 200 ? '...' : ''}</pre>
                            </div>
                          )}
                        </div>
                        <div className={styles.stageActions}>
                          <button 
                            className={styles.stageViewButton} 
                            title="View Details"
                            onClick={() => setSelectedStageOutput(stage)}
                          >
                            <Eye size={14} />
                          </button>
                        </div>
                      </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Stage Output Modal */}
                {selectedStageOutput && (
                  <div className={styles.stageOutputModal}>
                    <div className={styles.stageOutputModalContent}>
                      <div className={styles.stageOutputModalHeader}>
                        <h4>Stage Output: {selectedStageOutput.name}</h4>
                        <button 
                          className={styles.closeButton}
                          onClick={() => setSelectedStageOutput(null)}
                        >
                          ×
                        </button>
                      </div>
                      <div className={styles.stageOutputModalBody}>
                        {selectedStageOutput.output ? (
                          <pre className={styles.outputContent}>{selectedStageOutput.output}</pre>
                        ) : (
                          <p className={styles.noOutput}>No output available for this stage.</p>
                        )}
                        {selectedStageOutput.error && (
                          <div className={styles.outputError}>
                            <strong>Error:</strong> {selectedStageOutput.error}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className={styles.noRunSelected}>
                <History size={48} />
                <h4>Select a run to view details</h4>
                <p>Click on any run from the list to see its execution stages and details.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render template details page
  const renderTemplateDetails = () => {
    if (!selectedTemplate) return null;

    return (
      <div className={styles.templateDetailsPage}>
        <div className={styles.templateDetailsHeader}>
          <BackButton onClick={handleBack} />
          <h1>Workflow Template Details</h1>
          <button 
            className={styles.createButton}
            onClick={() => handleCreateFromTemplate(selectedTemplate)}
          >
            <Plus size={18} />
            <span>Create Workflow</span>
          </button>
        </div>

        <div className={styles.templateDetailsContent}>
          <div className={styles.templateDetailsMain}>
            <div className={styles.templateDetailsIcon}>
              {selectedTemplate.iconUrl ? (
                <img src={selectedTemplate.iconUrl} alt={selectedTemplate.name} />
              ) : (
                <div className={styles.defaultIconLarge}><Zap size={36} /></div>
              )}
            </div>
            <div className={styles.templateDetailsInfo}>
              <h2>{selectedTemplate.name}</h2>
              <p className={styles.templateDetailsAuthor}>
                by {sanitizeBrandText(selectedTemplate.author)} | <Users size={14} /> Shared with Project
              </p>
            </div>
          </div>

          <div className={styles.templateDetailsSection}>
            <h3>About workflow:</h3>
            <p>{selectedTemplate.description}</p>
          </div>

          <div className={styles.templateDetailsSection}>
            <div className={styles.yamlHeader}>
              <span>yaml</span>
              <div className={styles.yamlActions}>
                <button className={styles.yamlButton}>
                  <Copy size={14} />
                  Copy
                </button>
                <button className={styles.yamlButton}>
                  <Download size={14} />
                  Download
                </button>
              </div>
            </div>
            <div className={styles.yamlPreview}>
              <pre>{`assistants:
  - assistant_id: PRECONFIGURED:example-assistant
    id: main
enable_summarization_node: false
max_concurrency: 1
recursion_limit: 300
retry_policy:
  backoff_factor: 5
  initial_interval_seconds: 5
  max_attempts: 9
  max_interval_seconds: 50
states:
  - assistant_id: main
    id: initializer
    next:
      state_id: end
    task: |
      Execute the workflow task...`}</pre>
            </div>
          </div>

          <div className={styles.templateDetailsOverview}>
            <h3>OVERVIEW</h3>
            <div className={styles.overviewItem}>
              <span className={styles.overviewLabel}>Project:</span>
              <span className={styles.overviewValue}>umapathi@eclatansys.com</span>
            </div>
            <div className={styles.overviewItem}>
              <span className={styles.overviewLabel}>LINK TO WORKFLOW TEMPLATE</span>
              <div className={styles.linkInput}>
                <input 
                  type="text" 
                  value={`https://bringiq.eclatansys.com/#/workflows/templates/${selectedTemplate.id}`}
                  readOnly
                />
                <button className={styles.copyLinkButton}>
                  <Copy size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render list view
  const renderListView = () => (
    <div className={styles.listViewContent}>
      <div className={styles.contentHeader}>
          <div className={styles.headerLeft}>
            <span className={styles.itemCount}>
              {selectedCategory === 'templates' ? `${templateCount} TEMPLATES` : `${workflows.length} WORKFLOWS`}
            </span>
          </div>
          <div className={styles.headerRight}>
            <Input
              isSearch
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className={styles.viewToggle}>
              <button 
                className={viewMode === 'grid' ? styles.active : ''}
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 size={16} />
              </button>
              <button 
                className={viewMode === 'list' ? styles.active : ''}
                onClick={() => setViewMode('list')}
              >
                <List size={16} />
              </button>
            </div>
            <button className={styles.createWorkflowButton}>
              <Plus size={16} />
              Create Workflow
            </button>
          </div>
        </div>

        <div className={`${styles.itemsGrid} ${viewMode === 'list' ? styles.listView : ''}`}>
          {loading || initialLoad ? (
            <div className={styles.loading}>
              <Spinner size="md" label="Loading workflows..." />
            </div>
          ) : fetchError ? (
            <div className={styles.error}>
              <AlertCircle size={24} />
              <p>{fetchError}</p>
              <button onClick={() => fetchData()} className={styles.retryButton}>
                <RefreshCw size={16} />
                Retry
              </button>
            </div>
          ) : selectedCategory === 'templates' ? (
            templates.length > 0 ? (
              templates.map(renderTemplateCard)
            ) : (
              <div className={styles.empty}>No templates found</div>
            )
          ) : (
            workflows.length > 0 ? (
              workflows.map(renderWorkflowCard)
            ) : (
              <div className={styles.empty}>
                <p>No workflows yet</p>
                <button 
                  className={styles.createFirstButton}
                  onClick={() => setSelectedCategory('templates')}
                >
                  Browse Templates
                </button>
              </div>
            )
          )}
        </div>
    </div>
  );

  // Convert workflow metaStates to React Flow nodes
  const getInitialNodes = () => {
    if (!currentWorkflow?.metaStates) return [];
    
    // Create a map of stateId -> state config for quick lookup
    const statesMap: Record<string, any> = {};
    if (currentWorkflow.states) {
      currentWorkflow.states.forEach((state: any) => {
        statesMap[state.id] = state;
      });
    }
    
    // Create a map of workflow assistant alias -> actual assistantId
    const assistantAliasMap: Record<string, string> = {};
    if (currentWorkflow.assistants) {
      currentWorkflow.assistants.forEach((a: any) => {
        assistantAliasMap[a.id] = a.assistantId;
      });
    }
    
    return currentWorkflow.metaStates.map((metaState: any) => {
      // Find the corresponding state configuration
      const stateConfig = statesMap[metaState.id] || statesMap[metaState.data?.stateId];
      
      // Resolve the actual assistantId from alias
      let resolvedAssistantId = stateConfig?.assistantId;
      if (resolvedAssistantId && assistantAliasMap[resolvedAssistantId]) {
        resolvedAssistantId = assistantAliasMap[resolvedAssistantId];
      }
      
      return {
        id: metaState.id,
        type: metaState.type,
        position: metaState.position || { x: 0, y: 0 },
        data: {
          label: metaState.data?.label || metaState.id.charAt(0).toUpperCase() + metaState.id.slice(1).replace(/_/g, ' '),
          stateId: stateConfig?.id || metaState.data?.stateId || metaState.id,
          assistantId: resolvedAssistantId || metaState.data?.assistantId,
          task: stateConfig?.task || metaState.data?.task,
          outputSchema: stateConfig?.output_schema || stateConfig?.outputSchema || metaState.data?.outputSchema,
          ...metaState.data,
        },
      };
    });
  };

  // Convert workflow edges to React Flow edges
  const getInitialEdges = () => {
    if (!currentWorkflow?.edges) return [];
    return currentWorkflow.edges.map((edge: any) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'smoothstep',
      animated: true,
    }));
  };

  // Sidebar content for AppLayout (matching Chats/Assistants pattern)
  const workflowSidebar = pageMode === 'list' ? (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <div className={styles.sidebarHeaderTop}>
          <div>
            <h1 className={styles.sidebarTitle}>Workflows</h1>
            <p className={styles.sidebarSubtitle}>Browse and run available AI-powered workflows</p>
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            leftIcon={<Plus size={16} />}
            onClick={() => setPageMode('editor')}
          >
            Create
          </Button>
        </div>
      </div>

      <div className={styles.categories}>
        <h3 className={styles.sectionTitle}>CATEGORIES</h3>
        <nav className={styles.categoryNav}>
          {CATEGORIES.map(category => (
            <button
              key={category.id}
              className={`${styles.categoryItem} ${selectedCategory === category.id ? styles.active : ''}`}
              onClick={() => setSelectedCategory(category.id)}
            >
              {CATEGORY_ICONS[category.id]}
              <span>{category.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <div className={styles.filters}>
        <h3 className={styles.sectionTitle}>FILTERS</h3>

        <div className={styles.filterGroup}>
          <Input
            isSearch
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
          />
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>STATUS</span>
          </button>
          <div className={styles.filterContent}>
            <select className={styles.filterSelect}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>CREATED BY</span>
          </button>
          <div className={styles.filterContent}>
            <select className={styles.filterSelect}>
              <option value="">Created By</option>
            </select>
          </div>
        </div>

        <div className={styles.filterGroup}>
          <button className={styles.filterHeader}>
            <ChevronDown size={14} />
            <span>SHARED</span>
          </button>
        </div>
      </div>
    </div>
  ) : undefined;

  return (
    <AppLayout hideLayout={pageMode === 'editor'} sidebar={workflowSidebar}>
      {/* Notification Toast */}
      {notification && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.type === 'success' && <CheckCircle2 size={18} />}
          {notification.type === 'error' && <XCircle size={18} />}
          {notification.type === 'info' && <Spinner size="sm" />}
          <span>{notification.message}</span>
        </div>
      )}
      
      {pageMode === 'list' && renderListView()}
      {pageMode === 'template-details' && renderTemplateDetails()}
      {pageMode === 'run-history' && renderRunHistory()}
      {pageMode === 'editor' && currentWorkflow && (
        <WorkflowEditor
          workflowId={currentWorkflow.id}
          workflowName={currentWorkflow.name}
          initialNodes={getInitialNodes()}
          initialEdges={getInitialEdges()}
          onSave={handleSaveWorkflow}
          onSaveAndRun={handleSaveAndRunWorkflow}
          onClose={handleBack}
        />
      )}
    </AppLayout>
  );
};

export default WorkflowsPage;
